import React, { useMemo } from 'react';
import { Box, BoxProps } from '@mui/material';
import { useGlobalHooks } from '@app/core/hooks/global-hooks';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { atom_tourState } from '@app/shared/state/tour.atom';

export type TourPinProps = {
  id?: string;
  size: number;
  active?: boolean;
  children?: React.ReactNode;
  visited?: boolean;
  style?: React.CSSProperties;
  index?: number;
  disabled?: boolean;
  pinStrokeColor?: string;
  pinClassName?: string;
  isDesktop?: boolean;
} & BoxProps;

export function TourPinV2(props: TourPinProps & BoxProps) {
  const _g = useGlobalHooks();
  const { t, i18n } = useTranslation();
  const activeTour = useRecoilValue(atom_tourState);
  const { uID, tID, gID } = JSON.parse(localStorage.getItem('credential_items') || '{}');
  const getPinColor = () => {
    return activeTour?.config?.pinColor || _g.theme.palette.primary.main;
  };

  const strokeColor = props.pinStrokeColor || getPinColor();
  const effectiveSize = props.size;

  const isDemo = useMemo(() => {
    return uID?.toLowerCase() === 'demo';
  }, [uID, tID, gID]);

  // Variant and transit color constants
  const variant = (props.pinClassName || '').toLowerCase().includes('transit') ? 'transit' : 'tour';
  const TRANSIT_BG = '#FAE9DA';
  const TRANSIT_ICON = '#d25529';
  const pinColor = strokeColor;
  const pinColorActive = pinColor;
  const pinColorInactive = 'white';
  const strokeForSvg = variant === 'transit' ? TRANSIT_ICON : strokeColor;

  const tourBase = (fill: string) => `<?xml version="1.0" encoding="UTF-8"?>
<svg width="40" height="50" viewBox="0 0 40 50" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g id="PIN">
    <path id="ic" d="M22.7216 47.593L23.3333 48.3841L22.7216 47.593C22.4667 47.7901 22.2471 47.9715 22.0577 48.1278L22.0539 48.131L22.6906 48.9021L22.0539 48.131C21.6069 48.5001 21.3724 48.6863 21.1107 48.8096C20.8822 48.9173 20.5766 49 20 49C19.424 49 19.1113 48.9144 18.8703 48.7987C18.5937 48.666 18.3425 48.4653 17.8767 48.0797L17.8734 48.0769C17.6842 47.9203 17.4666 47.7402 17.2167 47.5451C13.3043 44.491 7.77459 37.4781 4.26329 31.1797C2.28437 27.6301 1 23.7888 1 20C1 9.50659 9.50659 1 20 1C30.4934 1 39 9.50659 39 20C39 23.7888 37.7156 27.6301 35.7367 31.1797C32.2059 37.513 26.6359 44.5663 22.7216 47.593Z" fill="${fill}" stroke="${strokeForSvg}" stroke-width="2"/>
  </g>
</svg>`;

  const transitBase = (fill: string) => `<?xml version="1.0" encoding="UTF-8"?>
<svg width="40" height="50" viewBox="0 0 40 50" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g id="PIN">
    <path id="ic" d="M22.7216 47.593L23.3333 48.3841L22.7216 47.593C22.4667 47.7901 22.2471 47.9715 22.0577 48.1278L22.0539 48.131L22.6906 48.9021L22.0539 48.131C21.6069 48.5001 21.3724 48.6863 21.1107 48.8096C20.8822 48.9173 20.5766 49 20 49C19.424 49 19.1113 48.9144 18.8703 48.7987C18.5937 18.666 18.3425 48.4653 17.8767 48.0797L17.8734 48.0769C17.6842 47.9203 17.4666 47.7402 17.2167 47.5451C13.3043 44.491 7.77459 37.4781 4.26329 31.1797C2.28437 27.6301 1 23.7888 1 20C1 9.50659 9.50659 1 20 1C30.4934 1 39 9.50659 39 20C39 23.7888 37.7156 27.6301 35.7367 31.1797C32.2059 37.513 26.6359 44.5663 22.7216 47.593Z" fill="${fill}" stroke="${strokeForSvg}" stroke-width="2"/>
    <circle cx="20" cy="18" r="4" fill="${TRANSIT_ICON}" />
  </g>
</svg>`;

  const computedFill = variant === 'transit' ? TRANSIT_BG : ((props.active || props.visited) ? pinColorActive : pinColorInactive);
  const svgBase = variant === 'transit' ? transitBase(computedFill) : tourBase(computedFill);
  const svgSrc = `data:image/svg+xml;utf8,${encodeURIComponent(svgBase)}`;

  // Choose anchoring strategy per variant: keep existing size-based offsets for
  // 'tour' pins and use transform-centering for 'transit' pins
  const anchorStyle: any =
    variant === 'tour'
      ? {
        position: 'relative',
        top: 0,
        left: i18n.dir() === 'rtl' ? 'unset' : effectiveSize * -0.5,
        right: i18n.dir() === 'rtl' ? effectiveSize * -0.5 : 'unset',
        transform: 'translateY(-100%)',
        zIndex: '999999 !important',
      }
      : {
        position: 'relative',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -100%)',
        zIndex: '999999 !important',
      };

  return (
    <Box
      className={`${props.pinClassName ? props.pinClassName : 'tour-pin'} stop-id-${props.id
        } inline-block relative origin-bottom`}
      sx={{
        ...anchorStyle,
        ...(props.active ? {
          animation: 'pinBounce 1s ease-in-out infinite',
          '@keyframes pinBounce': {
            '0%, 100%': { transform: `${anchorStyle.transform || ''} translateY(0)`.trim() },
            '50%': { transform: `${anchorStyle.transform || ''} translateY(-6px)`.trim() },
          },
        } : {}),
      }}
    >
      {/* Inline SVG rendering (tour vs transit) */}
      {variant === 'transit' ? (
        <svg
          width={effectiveSize}
          height={effectiveSize}
          viewBox="0 0 40 50"
          xmlns="http://www.w3.org/2000/svg"
          style={{ filter: `drop-shadow(0px 0px 4px ${strokeForSvg})` }}
          role="img"
          aria-label={`${variant} pin`}
        >
          <g id="PIN">
            <path
              id="ic"
              d="M22.7216 47.593L23.3333 48.3841L22.7216 47.593C22.4667 47.7901 22.2471 47.9715 22.0577 48.1278L22.0539 48.131L22.6906 48.9021L22.0539 48.131C21.6069 48.5001 21.3724 48.6863 21.1107 48.8096C20.8822 48.9173 20.5766 49 20 49C19.424 49 19.1113 48.9144 18.8703 48.7987C18.5937 48.666 18.3425 48.4653 17.8767 48.0797L17.8734 48.0769C17.6842 47.9203 17.4666 47.7402 17.2167 47.5451C13.3043 44.491 7.77459 37.4781 4.26329 31.1797C2.28437 27.6301 1 23.7888 1 20C1 9.50659 9.50659 1 20 1C30.4934 1 39 9.50659 39 20C39 23.7888 37.7156 27.6301 35.7367 31.1797C32.2059 37.513 26.6359 44.5663 22.7216 47.593Z"
              fill={computedFill}
              stroke={strokeForSvg}
              strokeWidth={2}
            />
            <circle cx="20" cy="18" r="4" fill={TRANSIT_ICON} />
          </g>
        </svg>
      ) : (
        <svg
          width={effectiveSize}
          height={effectiveSize}
          viewBox="0 0 40 50"
          xmlns="http://www.w3.org/2000/svg"
          style={{ filter: `drop-shadow(0px 0px 4px ${strokeForSvg})` }}
          role="img"
          aria-label={`${variant} pin`}
        >
          <g id="PIN">
            <path
              id="ic"
              d="M22.7216 47.593L23.3333 48.3841L22.7216 47.593C22.4667 47.7901 22.2471 47.9715 22.0577 48.1278L22.0539 48.131L22.6906 48.9021L22.0539 48.131C21.6069 48.5001 21.3724 48.6863 21.1107 48.8096C20.8822 48.9173 20.5766 49 20 49C19.424 49 19.1113 48.9144 18.8703 48.7987C18.5937 48.666 18.3425 48.4653 17.8767 48.0797L17.8734 48.0769C17.6842 47.9203 17.4666 47.7402 17.2167 47.5451C13.3043 44.491 7.77459 37.4781 4.26329 31.1797C2.28437 27.6301 1 23.7888 1 20C1 9.50659 9.50659 1 20 1C30.4934 1 39 9.50659 39 20C39 23.7888 37.7156 27.6301 35.7367 31.1797C32.2059 37.513 26.6359 44.5663 22.7216 47.593Z"
              fill={computedFill}
              stroke={strokeForSvg}
              strokeWidth={2}
            />
          </g>
        </svg>
      )}
      <Box
        className="flex items-center justify-center"
        sx={{
          width: effectiveSize,
          height: effectiveSize,
          position: 'absolute',
          top: 0,
          // White: mobile active (any) OR visited (not active). Otherwise → strokeColor
          color: variant === 'transit'
            ? TRANSIT_ICON
            : (((props.active && !props.isDesktop) || (props.visited && !props.active)) ? 'white' : strokeColor),
          fontSize: props.isDesktop && props.active ? '14px' : (typeof props.children === 'string' ? '1.3em' : '1em'),
          fontWeight: props.active || props.visited ? 800 : 600,
          fontFamily: _g.theme.typography.fontFamily,
          pointerEvents: props.disabled ? 'none' : 'auto',
          opacity: props.disabled ? 0.7 : 1,
        }}
      >
        {props.isDesktop && props.active ? (() => {
          // Geometry assumptions from original SVG (circle ~80% of height, tail ~20%)
          const WHITE_DIAMETER = 24; // inner white circle diameter
          const circleDiameter = effectiveSize * 0.8;
          // Position white circle so it centers within circular top, not entire pin
          const topOffset = (circleDiameter - WHITE_DIAMETER) / 2; // shift upward relative to whole pin
          return (
            <Box
              sx={{
                width: WHITE_DIAMETER,
                height: WHITE_DIAMETER,
                backgroundColor: 'white',
                borderRadius: '50%',
                position: 'absolute',
                top: topOffset,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 200,
                boxShadow: `0 0 0 2px ${props.pinStrokeColor}`,
              }}
            >
              {props.children}
            </Box>
          );
        })() : (
          <Box sx={{ zIndex: 200 }}>{props.children}</Box>
        )}
      </Box>
    </Box>
  );
}
